import { useCallback, useEffect, useState } from "react";
import {
  EHappyHourLocation,
  EHappyHourMessageDisplayTiming,
  HappyHourData,
} from "../types/HappyHour.interface";
import useCartModelChange from "@src/backbone/hooks/useCartModelChange";
import useCustomerModelChange from "@src/backbone/hooks/useCustomerModelChange";
import { HappyHourModelAttributes } from "@src/backbone/interface/HappyHourAttribute";
import { useConfiguration } from "@src/hooks/query/useConfiguration";
import PaymentModeModel from "@src/backbone/model/PaymentModeModel";
import { useTranslation } from "@src/hooks/useTranslation";

function mapApiToHappyHourData(
  apiResponse?: HappyHourModelAttributes,
  extraProps?: {
    url?: string;
    offlinePaymentMention?: string;
  }
): HappyHourData {
  const active = apiResponse?.is_now_happy_hour;
  const startDate = apiResponse?.start_date;
  const endDate = apiResponse?.end_date;
  const endTime = apiResponse?.end_date?.replace(/^.*(..:..):..$/, "$1");

  const messages = apiResponse?.messages;
  const { offlinePaymentMention, url, ...restProps } = extraProps || {};
  return [
    EHappyHourLocation.LISTING_DEFAULT,
    EHappyHourLocation.LISTING_WARN,
    EHappyHourLocation.CART_INFO,
    EHappyHourLocation.PAYMENT_INFO,
  ].reduce((acc, locationKey) => {
    const message = messages?.[locationKey];
    return {
      ...acc,
      [locationKey]: {
        ...restProps,
        ...(locationKey === EHappyHourLocation.LISTING_DEFAULT && {
          url, // seule la bannière listing est cliquable
        }),
        ...(!!offlinePaymentMention &&
          locationKey === EHappyHourLocation.PAYMENT_INFO && {
            mention: offlinePaymentMention.replace("%s", endTime || ""),
          }),
        id_happy_hour_message: locationKey,
        location: locationKey,
        name: "",
        message: message?.message || "",
        active,
        start_date: startDate,
        end_date: endDate,
        display_timing:
          locationKey === EHappyHourLocation.LISTING_WARN
            ? EHappyHourMessageDisplayTiming.WARNING_TIME // alerte listing produits
            : EHappyHourMessageDisplayTiming.ALL_TIME, // autres emplacements
      },
    };
  }, {});
}

export function useHappyHourMessage(): {
  data: HappyHourData | undefined;
  loading: boolean;
  error: any;
  fetchHappyHour: () => Promise<void>;
} {
  const [, cart] = useCartModelChange(["id_cart", "id_store"]);
  const [, customer] = useCustomerModelChange(["id_customer"]);
  const [data, setData] = useState<HappyHourData | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const { getConfiguration } = useConfiguration();
  const { t } = useTranslation(["HappyHour"]);
  const [isOnlinePayment, setIsOnlinePayment] = useState<boolean>(
    !!cart.getUsedPaymentMode()?.isOnlinePayment()
  );
  const landingPageHappyHourUrl = getConfiguration(
    "_HAPPY_HOUR_LANDING_PAGE_URL_",
    ""
  );
  const offlinePaymentMention = isOnlinePayment
    ? ""
    : cart.isNow()
    ? t(
        "Attention : vos points seront augmentés si votre commande est réglée en shop avant %s.",
        "HappyHour"
      )
    : t(
        "Attention : vos points seront crédités si vous réglez votre commande sur le site. Le paiement à la réception ne permet pas d'augmenter vos points.",
        "HappyHour"
      );

  const handleHappyHourSynced = useCallback(
    (model: any) => {
      const response = model.toJSON() as HappyHourModelAttributes;
      const mappedData = mapApiToHappyHourData(response, {
        url: landingPageHappyHourUrl,
        offlinePaymentMention,
      });
      setData(mappedData);
    },
    [offlinePaymentMention, landingPageHappyHourUrl]
  );

  const fetchHappyHour = useCallback(async () => {
    if (!cart) return;
    setLoading(true);

    try {
      const model = await cart.getHappyHour().fetchWithCache();
      handleHappyHourSynced(model);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [cart, handleHappyHourSynced]);

  useEffect(() => {
    const onUsePaymentMode = (paymentMode: PaymentModeModel) => {
      setIsOnlinePayment(!!paymentMode?.isOnlinePayment());
    };
    cart.on("usePaymentMode", onUsePaymentMode);
    return () => {
      cart.off("usePaymentMode", onUsePaymentMode);
    };
  }, [cart]);

  useEffect(() => {
    fetchHappyHour();
    if (!cart || !customer) return;

    customer.on("change:id_customer", fetchHappyHour);
    customer.on("change:segmentation", fetchHappyHour);
    const happyHourModel = cart.getHappyHour();
    happyHourModel.on("happyHourSynced", handleHappyHourSynced);

    return () => {
      customer.off("change:id_customer", fetchHappyHour);
      customer.off("change:segmentation", fetchHappyHour);
      happyHourModel.off("happyHourSynced", handleHappyHourSynced);
    };
  }, [cart, customer, fetchHappyHour, handleHappyHourSynced]);

  return { data, loading, error, fetchHappyHour };
}
